import { apiDownload, apiGet, apiPost, apiUpload } from '../../lib/api'
import type { ComplianceState } from '../brokers/api'
import { withAuth } from '../auth/token'

/** Where a rate confirmation's reading stands (Stage 3, 3.1); the review's statuses arrive with 3.2. */
export type RateConfirmationStatus = 'Reading' | 'ReadyForReview' | 'ReadFailed'

/** In the order the list's filter offers them. */
export const RATE_CONFIRMATION_STATUSES: readonly RateConfirmationStatus[] = ['Reading', 'ReadyForReview', 'ReadFailed']

/** How one reading ended. Every outcome but `Read` leaves the record with nothing read (D5). */
export type ReadOutcome =
  | 'Read'
  | 'Refused'
  | 'CutOff'
  | 'Unavailable'
  | 'Unreadable'
  | 'NotARateConfirmation'
  | 'Interrupted'

/** Why a reading ran: the upload, the one automatic careful re-read, or a person asking. */
export type ReadTrigger = 'Arrival' | 'Escalation' | 'Requested'

export interface RateConfirmationSummary {
  id: string
  fileName: string
  status: RateConfirmationStatus
  failure: ReadOutcome | null
  receivedAt: string
  receivedByEmail: string | null
  /** How many rate confirmations the reading found in the file; null until one reads. */
  documents: number | null
  brokerName: string | null
  loadNumber: string | null
  total: number | null
  currency: string | null
}

/** Why a value is null: the document does not state it, or states it and it cannot be read. */
export type MissingReason = 'NotOnDocument' | 'Illegible'

/** One fact read off the document, with its evidence — the page and the words that show it. */
export interface ReadField<T> {
  value: T | null
  page: number | null
  quote: string | null
  missing: MissingReason | null
}

export type ReadEquipmentType = 'DryVan' | 'Reefer' | 'Flatbed' | 'StepDeck' | 'DoubleDrop' | 'StraightBoxTruck' | 'Other'
export type ReadEquipmentFeature = 'AirRide' | 'LiftGate' | 'Vented' | 'Conestoga' | 'Tarps' | 'Chains' | 'Straps'
export type ReadLoadType = 'Full' | 'Partial'
export type ReadChargeKind =
  | 'Fuel'
  | 'ExtraStop'
  | 'Detention'
  | 'Layover'
  | 'Lumper'
  | 'DriverAssist'
  | 'TruckOrderedNotUsed'
  | 'Tarp'
  | 'Other'
export type ReadStopKind = 'Pickup' | 'Delivery'
export type ReadAppointmentKind = 'Appointment' | 'Fcfs'
export type ReadReturnMethod = 'Email' | 'Fax' | 'Website'
export type ReadSignatureAsk =
  | 'SignerName'
  | 'SignatureDate'
  | 'DriverName'
  | 'DriverPhone'
  | 'TruckNumber'
  | 'TrailerNumber'
  | 'Other'

export interface ReadCharge {
  kind: ReadChargeKind
  description: string
  amount: number | null
  page: number | null
  quote: string | null
}

export interface ReadStop {
  sequence: number
  kind: ReadStopKind
  company: ReadField<string>
  street: ReadField<string>
  city: ReadField<string>
  state: ReadField<string>
  zip: ReadField<string>
  /** YYYY-MM-DD. */
  date: ReadField<string>
  /** YYYY-MM-DD, only when the document gives a window over several days. */
  lastDate: ReadField<string>
  /** 24-hour HH:MM, as written at the stop — no time-zone conversion (D9). */
  timeFrom: ReadField<string>
  timeTo: ReadField<string>
  appointment: ReadField<ReadAppointmentKind>
  references: { label: string; value: string; page: number | null }[]
  notes: ReadField<string>
}

/** One rate confirmation within the file, field by field — the reading as the model gave it. */
export interface ReadDocument {
  firstPage: number | null
  lastPage: number | null
  broker: {
    name: ReadField<string>
    mcNumber: ReadField<string>
    usdotNumber: ReadField<string>
    address: ReadField<string>
    agentName: ReadField<string>
    agentEmail: ReadField<string>
    agentPhone: ReadField<string>
  }
  carrier: {
    name: ReadField<string>
    mcNumber: ReadField<string>
    usdotNumber: ReadField<string>
  }
  loadNumber: ReadField<string>
  issuedOn: ReadField<string>
  equipment: {
    asPrinted: ReadField<string>
    types: ReadEquipmentType[]
    features: ReadEquipmentFeature[]
    lengthFeet: ReadField<number>
  }
  freight: {
    loadType: ReadField<ReadLoadType>
    weightPounds: ReadField<number>
    pieces: ReadField<number>
    pallets: ReadField<number>
    commodity: ReadField<string>
    miles: ReadField<number>
    temperature: ReadField<string>
    hazmat: ReadField<boolean>
  }
  money: {
    lineHaul: ReadField<number>
    accessorials: ReadCharge[]
    total: ReadField<number>
    currency: ReadField<string>
  }
  stops: ReadStop[]
  terms: {
    accessorialTerms: ReadField<string>
    paymentTerms: ReadField<string>
    invoiceTo: ReadField<string>
    returnMethod: ReadField<ReadReturnMethod>
    returnTo: ReadField<string>
    signatureAsks: ReadSignatureAsk[]
  }
}

export interface RateConfReading {
  pageCount: number | null
  documents: ReadDocument[]
}

/** One reading of the file: which model, why, how it ended and what it cost. */
export interface ReadingAttempt {
  sequence: number
  trigger: ReadTrigger
  model: string
  servedModel: string | null
  effort: string | null
  outcome: ReadOutcome
  detail: string | null
  inputTokens: number | null
  outputTokens: number | null
  estimatedCostUsd: number | null
  durationMilliseconds: number
  startedAt: string
  /** Whether the record shows this attempt's reading. */
  current: boolean
}

/**
 * Something the platform noticed (D4): a code and its values, never a sentence. `field` is the
 * reading's own path (`money.total`, `stops[2].date`); `stop` counts from 1.
 */
export interface ReadingFlag {
  code: string
  field: string | null
  stop: number | null
  values: Record<string, string>
}

/** A broker of the book the rate confirmation points at, and what pointed at it. */
export interface BrokerOption {
  brokerId: string
  name: string
  mcNumber: string | null
  complianceState: ComplianceState
  /** `Mc`, `Usdot`, `Agent`, `Broker`, `Learned`, `Domain` or `Name`. */
  foundBy: string
  evidence: string | null
  agentName: string | null
  agentId: string | null
}

export type BrokerResolutionStatus = 'Known' | 'Ambiguous' | 'Candidate' | 'Suggested' | 'Unknown'

export interface BrokerResolution {
  key: string
  status: BrokerResolutionStatus
  brokers: BrokerOption[]
  /** Brokers that are inactive and that the document points at: said, never offered. */
  stoodDown: BrokerOption[]
}

export interface CarrierOption {
  carrierId: string
  name: string
  mcNumber: string | null
  usdotNumber: string | null
  complianceState: ComplianceState
  /** `Mc`, `Usdot` or `Name`. */
  foundBy: string
}

export type CarrierResolutionStatus = 'Known' | 'Ambiguous' | 'Suggested' | 'Unknown'

export interface CarrierResolution {
  status: CarrierResolutionStatus
  carriers: CarrierOption[]
  stoodDown: CarrierOption[]
}

/** The platform's look at one rate confirmation of the file, computed when the record is read. */
export interface DocumentReview {
  index: number
  flags: ReadingFlag[]
  broker: BrokerResolution
  /** The broker's agent the printed contact names; null when it names none of them. */
  agent: BrokerOption | null
  carrier: CarrierResolution
}

export interface RateConfirmationDetail {
  id: string
  fileName: string
  sizeBytes: number
  source: 'Upload' | 'Email'
  status: RateConfirmationStatus
  failure: ReadOutcome | null
  /** The service's own words about a failed reading, in English; null otherwise. */
  failureDetail: string | null
  receivedAt: string
  receivedByEmail: string | null
  readRequestedAt: string
  /** The model the reading in progress, or the next one, asks. */
  pendingModel: string
  /** Null while nothing has read — a failed reading offers nothing as read (D5). */
  reading: RateConfReading | null
  attempts: ReadingAttempt[]
  version: string
  /** One per rate confirmation the reading found; null while there is no reading. */
  review: DocumentReview[] | null
}

export function listRateConfirmations(status?: RateConfirmationStatus): Promise<RateConfirmationSummary[]> {
  const query = status === undefined ? '' : `?status=${status}`
  return apiGet<RateConfirmationSummary[]>(`/api/rate-confirmations${query}`, { headers: withAuth() })
}

export function getRateConfirmation(id: string): Promise<RateConfirmationDetail> {
  return apiGet<RateConfirmationDetail>(`/api/rate-confirmations/${encodeURIComponent(id)}`, { headers: withAuth() })
}

/** Files the PDF and asks for its reading; answers at once, with the record reading. */
export function uploadRateConfirmation(file: File): Promise<RateConfirmationDetail> {
  const form = new FormData()
  form.append('file', file, file.name)
  return apiUpload<RateConfirmationDetail>('/api/rate-confirmations', form, { headers: withAuth() })
}

/** Asks for a careful reading on the escalation model; refused while one is on its way. */
export function readRateConfirmationAgain(id: string): Promise<RateConfirmationDetail> {
  return apiPost<RateConfirmationDetail>(
    `/api/rate-confirmations/${encodeURIComponent(id)}/read-again`,
    {},
    { headers: withAuth() },
  )
}

/** The PDF as it arrived — guarded by the bearer token, so it comes back as bytes. */
export function fetchRateConfirmationDocument(id: string): Promise<Blob> {
  return apiDownload(`/api/rate-confirmations/${encodeURIComponent(id)}/document`, { headers: withAuth() })
}
